import { Component, inject } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { UserService } from '../shared/user/user.service';
import { BaseResponse, ResponseResultCode } from '@aifs-shared/common/base-response';
import { UiModalService } from '@ui/ui-modal.service';
import { MetaForm, MetaFormDisplayComponent, MetaFormService, MetaFormTextType, MetaFormUserEvent, MFValidator, MFValueChange, UserEventType } from '@aifs-metaform/public-api';
import { Subscription } from 'rxjs';
import { CfButtonComponent, CFButtonStyle, CFButtonType } from '@ui/cf-button/cf-button.component';

@Component({
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    imports: [MetaFormDisplayComponent, CfButtonComponent]
})

export class ResetPasswordComponent {// {implements OnInit, MetaformDataProvider {

    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private modalService = inject(UiModalService);
    private userService = inject(UserService);
    private metaformService = inject(MetaFormService);

    ButtonStyle = CFButtonStyle;
    ButtonType = CFButtonType;

    constructor() { }

    ngOnInit() {
        // Check that there is a signed in 'user'
        const signedInAs = this.userService.userId();
        const lookingAt = +this.route.snapshot.params['userId'];

        if (signedInAs !== lookingAt) {
            // console.log(`SignedInAs ${signedInAs}, lookingFor: ${lookingAt}`);
            this.router.navigateByUrl('/sign-in');
            return;
        }

        this.createForm();

        // In case there is a forced update
        this.forcedUpdate = (this.userService.mustChangePassword());
        this.updateSource = this.userService.changePasswordSource();
    }

    ngOnDestroy() {
        this.formSubscription?.unsubscribe();
    }

    doResetPassword() {
        this.isProcessing = true;

        this.userService
            .resetPassword(this.newPassword, this.passwordConfirmation)
            .subscribe({
                next: (r: BaseResponse) => {
                    if (r.resultCode == ResponseResultCode.Success) {
                        const returnUrl = sessionStorage.getItem('returnUrl');
                        const param = (returnUrl ?? '').length > 0 ? `${returnUrl}` : `/home`;
                        const extraMessage = (this.userService.email() == undefined) ? " Please sign in with your new password." : "";

                        this.showModal("Change Password", `You have successfully updated your password!${extraMessage}`, undefined, "info", param);

                        // Tidy up
                        sessionStorage.removeItem('returnUrl');
                    } else {
                        this.showError("Change Password", "We couldn't update your password:", r.error);
                    }
                },
                error: (error: any) => {
                    let message = error;
                    if (error.message) {
                        message = error.message;
                    }
                    this.showError("Change Password", "There was an unexpected error attempting to update your password:", message);
                },
                complete: () => {
                    this.isProcessing = false;
                }
            });
    }

    showError(title: string, message: string, extra?: string) {
        this.modalService.showModalError(title, message);
    }

    showModal(title: string, message: string, extra: string | undefined = undefined, cssClass: string = "info", navigateToUrl: string | undefined = undefined) {
        let bodyExtra = extra ? `<p>${extra}</p>` : '';

        this.modalService.showModalInformation(title, `<p>${message}</p>${bodyExtra}`)
            .subscribe({
                next: (r: string) => {
                    if (this.userService.email() == undefined) {

                        this.userService.logout();
                        this.router.navigateByUrl('/sign-in');
                        return;
                    }

                    this.userService
                        .login(
                            this.userService.email(),
                            this.newPassword)
                        .subscribe({
                            next: (success: boolean) => {
                                this.form?.resetAllQuestions();

                                if (this.userService.isApplicant()) {
                                    if ((navigateToUrl ?? '').length > 0) {
                                        this.router.navigateByUrl(navigateToUrl!);
                                    } else {
                                        this.router.navigateByUrl('/home');
                                    }
                                }
                            },
                            error: (error: any) => {
                                console.error(error);
                            }
                        });
                }
            });
    }

    onFormEvent(event: MetaFormUserEvent): void {
        switch (event.type) {
            case UserEventType.FormInitialised:
            case UserEventType.FormInvalid:
            case UserEventType.FormInvalidOnAttemptedSubmit:
                this.updatePasswordButtonEnabled = false;
                break;
            case UserEventType.FormValid:
                // console.log(`The form is now valid`);
                this.updatePasswordButtonEnabled = true;
                break;
            case UserEventType.FormSubmit:
                if (this.updatePasswordButtonEnabled) this.doResetPassword();
                break;
        }
    }

    private createForm() {
        const f = this.metaformService.createForm('reset-password', 'Reset Password');

        f.addSection('Default');
        f.addQuestion('user_password')
            .addTextControl('newPassword', MetaFormTextType.Password, 255, 'new passwor')
            .addValidator(MFValidator.Required('Please enter a new password'));

        f.addQuestion('user_password_confirm')
            .addTextControl('passwordConfirmation', MetaFormTextType.Password, 255, 'confirm your password')
            .addValidator(MFValidator.Required('Please re-type your new password'))
            .addValidator(MFValidator.AnswerMustMatch('[newPassword]', 'New passwords must match!'));

        // console.log(f.toJson());

        this.formSubscription = f.change$.subscribe({
            next: (chg: MFValueChange) => {
                switch (chg.name) {
                    case 'newPassword':
                        this.newPassword = chg.value;
                        break;
                    case 'passwordConfirmation':
                        this.passwordConfirmation = chg.value;
                        break;
                }
            }
        });

        this.form = f;
    }

    title = '';
    signInName = '';

    forcedUpdate = false;
    updateSource = 0;
    updatePasswordButtonEnabled = false;
    passwordData?: PasswordResetData;

    form?: MetaForm;
    formSubscription?: Subscription;
    isProcessing = false;

    newPassword = '';
    passwordConfirmation = '';
}

export interface PasswordResetData {
    newPassword: string;
    passwordConfirmation: string;
}
